import { z } from 'zod'
import { viewItemListSchema } from '@integration-layer/schemas/GAEvents/viewItemList'
import { itemSchema } from '@integration-layer/schemas/GAEvents/index'
import snakeCase from 'lodash/snakeCase'

type ViewItemList = z.infer<typeof viewItemListSchema>
type AvailableItem = z.infer<typeof itemSchema>
type BooleanLiteral = 0 | 1
type firstSizeRecap = { size: string; quantity: number }

/**
 * Generates Google Analytics 4 view item list events for Visenze product recommendations.
 *
 * This composable prepares and sends tracking events for product recommendations
 * from Visenze, supporting different carousel types like "Complete the Look" and "Similar Products".
 *
 * @param {any[]} visenzeProducts - Array of Visenze product recommendations
 * @param {string} carouselType - Type of carousel (e.g., 'STL' for "Complete the Look", 'VSR' for "Similar Products")
 *
 * @returns {void}
 *
 * @throws {Error} Logs an error if the input products array is invalid
 *
 * @example
 * // Track Visenze product recommendations
 * useGAVisenzeViewItemListEvent(visenzeRecommendations, 'STL')
 *
 * @remarks
 * - Handles brand and size formatting
 * - Extracts product availability from size information
 * - Supports different carousel types with specific list names
 * - Processes complex product metadata
 */
export const useGAVisenzeViewItemListEvent = (
  visenzeProducts: any,
  carouselType: string
) => {
  // Input validation
  if (visenzeProducts.length === 0 || !Array.isArray(visenzeProducts)) {
    console.error('Invalid or missing visenze array.')
    return
  }

  // Transform Visenze products into GA-compatible product list
  const productList: Array<AvailableItem> = visenzeProducts.map(
    (item, index) => {
      // Brand formatting
      const item_brand = item?.data?.brand
        ? String(item.data.brand).toLowerCase() === 'ea7'
          ? 'EA7'
          : snakeCase(String(item.data.brand))
        : ''

      // Size and quantity processing
      const sizes = item.data?.size_list || []
      const sizesWithQuantity = sizes.map((size: string) => {
        // Extract avEcom value using regex
        const avEcomMatch = size.match(/avEcom: (\d+)/)
        return {
          size,
          quantity: avEcomMatch ? parseInt(avEcomMatch[1], 10) : 0,
        }
      })

      // Quantity and availability based on first size available
      const firstSizeInfo = <firstSizeRecap>sizesWithQuantity[0] || {
        size: '',
        quantity: 0,
      }

      const sizeParts = firstSizeInfo.size.split(', ')

      const sizeLabel =
        sizeParts
          .find((part: string) => part.startsWith('label:'))
          ?.replace('label:', '') || ''

      // Prepare product details for GA event
      return {
        affiliation: 'armani.com',
        coupon: '',
        discount: 0 as BooleanLiteral,
        index: index + 1,
        item_availability: sizesWithQuantity.find(
          (sizeInfo: firstSizeRecap) => sizeInfo.quantity > 0
        )
          ? 1
          : 0,
        item_brand: item_brand ?? '',
        item_category: '',
        item_category2: '',
        item_category4: '',
        item_discount: 0 as BooleanLiteral,
        item_id: item.product_id ?? '',
        item_img: item.main_image_url ?? '',
        item_list_id: '',
        item_list_name: '',
        item_mfc_id: item.product_id.replaceAll('_', ''),
        item_name: item.data?.title ?? '',
        item_reservable: 0 as BooleanLiteral,
        item_size: sizeLabel ?? '',
        item_variant: item.data?.color ?? '',
        price: parseFloat(item.data?.price?.value ?? 0),
        quantity: firstSizeInfo.quantity ?? 0,
        item_url: item.data.product_url,
      }
    }
  )

  // Send event for specific carousel types
  if (carouselType === 'STL' || carouselType === 'VSR') {
    const event: ViewItemList = {
      event: 'view_item_list',
      eventID: '001',
      ecommerce: {
        item_list_id:
          carouselType === 'STL' ? 'complete_the_look' : 'similar_products',
        item_list_name:
          carouselType === 'STL' ? 'complete_the_look' : 'similar_products',
        items: productList,
      },
    }
    useGASendEvent(viewItemListSchema, event, {
      clearEcommerce: true,
    })
  }
}
